import React, { useState } from 'react';
import {
  X,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  CreditCard,
  Calendar,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { api } from '../api/client';
import type { BillingStatusResponse } from '@shared/types';

interface BillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingStatus?: BillingStatusResponse | null;
  onRefreshBilling?: () => void;
}

export const BillingModal: React.FC<BillingModalProps> = ({
  isOpen,
  onClose,
  billingStatus,
  onRefreshBilling
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      onRefreshBilling?.();
    }
  }, [isOpen, onRefreshBilling]);

  if (!isOpen) return null;

  const isTrialActive = billingStatus?.is_trial && (billingStatus?.trial_days_remaining ?? 0) > 0;
  const isSubscriber = billingStatus?.status === 'active';
  const isExpired = billingStatus?.status === 'expired' || (!billingStatus?.has_access && !isSubscriber);
  const daysLeft = billingStatus?.trial_days_remaining ?? 14;

  const handleStartTrialOrSubscribe = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const res = await api.createCheckoutSession({
        plan: selectedPlan,
        success_url: `${window.location.origin}/?billing=success`,
        cancel_url: `${window.location.origin}/?billing=canceled`
      });

      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to initiate Stripe checkout');
      setIsLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const res = await api.createPortalSession();
      if (res.portal_url) {
        window.location.href = res.portal_url;
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to open Stripe customer portal');
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden transition-colors duration-200">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800/80">
          <div className="flex items-center space-x-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500 to-cyan-500 flex items-center justify-center text-white shadow-md shadow-indigo-500/20">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                BioPharm Regulatory Pro
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                ASQ CPGP & RAPS RAC Examination Suite
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 sm:p-8 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Status Alert Banner */}
          {isSubscriber ? (
            <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-emerald-900 dark:text-emerald-200">
                  Active Professional Subscription
                </p>
                <p className="text-emerald-700 dark:text-emerald-400 mt-0.5">
                  You have full unlimited access to all question banks and retention modules.
                </p>
              </div>
            </div>
          ) : isTrialActive ? (
            <div className="p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800/60 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0" />
                <div className="text-xs">
                  <p className="font-semibold text-indigo-900 dark:text-indigo-200">
                    14-Day Complimentary Free Trial Active
                  </p>
                  <p className="text-indigo-700 dark:text-indigo-300 mt-0.5">
                    {daysLeft} day{daysLeft === 1 ? '' : 's'} remaining in your trial period.
                  </p>
                </div>
              </div>
              <span className="text-[11px] font-mono font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white shadow-sm">
                {daysLeft}D LEFT
              </span>
            </div>
          ) : (
            <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-amber-900 dark:text-amber-200">
                  Trial Access Expired
                </p>
                <p className="text-amber-700 dark:text-amber-300 mt-0.5">
                  Upgrade to keep practicing with verified 21 CFR and EU GMP exam simulations.
                </p>
              </div>
            </div>
          )}

          {/* Error Banner */}
          {errorMessage && (
            <div className="p-3.5 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 flex items-start gap-2.5 text-xs text-rose-700 dark:text-rose-300">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 dark:text-rose-400 mt-0.5" />
              <div className="flex-1">{errorMessage}</div>
            </div>
          )}

          {/* 14-Day Free Trial Guarantee Card */}
          {!isSubscriber && (
            <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-indigo-500/10 via-cyan-500/10 to-transparent border border-indigo-200/80 dark:border-indigo-800/80">
              <div className="flex items-start space-x-3">
                <ShieldCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                <div className="text-xs space-y-1">
                  <span className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[11px]">
                    14-Day Free Trial Guarantee
                  </span>
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                    $0 charged today. Test all full-length timed exams, SM-2 retention drills, and regulatory rationales for 14 full days. Cancel anytime before your trial ends without being charged.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Plan Selector */}
          {!isSubscriber && (
            <div className="space-y-3">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider block">
                Select Your Plan
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Annual Plan (Recommended) */}
                <div
                  onClick={() => setSelectedPlan('annual')}
                  className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                    selectedPlan === 'annual'
                      ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50/40 dark:bg-indigo-950/30 shadow-sm'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
                  }`}
                >
                  <div className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-gradient-to-r from-indigo-600 to-cyan-600 text-[10px] font-bold text-white shadow">
                    SAVE 42%
                  </div>

                  <div className="text-xs font-semibold text-slate-900 dark:text-white flex items-center justify-between">
                    <span>Annual Pass</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">14 Days Free</span>
                  </div>

                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-slate-900 dark:text-white">$199</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">/year</span>
                  </div>

                  <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    Billed annually after 14-day trial ($16.58/mo).
                  </p>
                </div>

                {/* Monthly Plan */}
                <div
                  onClick={() => setSelectedPlan('monthly')}
                  className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                    selectedPlan === 'monthly'
                      ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50/40 dark:bg-indigo-950/30 shadow-sm'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
                  }`}
                >
                  <div className="text-xs font-semibold text-slate-900 dark:text-white flex items-center justify-between">
                    <span>Monthly Pass</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">14 Days Free</span>
                  </div>

                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-slate-900 dark:text-white">$29</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">/month</span>
                  </div>

                  <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    Billed monthly after 14-day trial. Cancel anytime.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Feature Inclusions Checklist */}
          <div className="space-y-2.5 pt-2">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
              Included in Your Pass
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-400">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Full ASQ CPGP & RAPS RAC Bank</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>FDA 21 CFR 210/211 & Annex 1 Citations</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>SM-2 Spaced Repetition Drill Engine</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Timed Exam Simulation with Domain Scores</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Daily Challenge Question & Streak Tracker</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Flutter Mobile App Access Sync</span>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80 space-y-3">
            {isSubscriber ? (
              <button
                onClick={handleOpenPortal}
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-2 py-3.5 px-4 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm shadow-md hover:shadow-lg transition cursor-pointer disabled:opacity-60"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    <span>Manage Billing & Invoices (Stripe Portal)</span>
                    <ExternalLink className="w-3.5 h-3.5 opacity-80" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleStartTrialOrSubscribe}
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-2 py-3.5 px-4 rounded-2xl bg-gradient-to-r from-indigo-600 via-indigo-700 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white font-bold text-sm shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition active:scale-[0.99] cursor-pointer disabled:opacity-60"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>
                      {isExpired
                        ? `Subscribe Now — $${selectedPlan === 'annual' ? '199/year' : '29/month'}`
                        : `Start 14-Day Free Trial ($0 Today)`}
                    </span>
                  </>
                )}
              </button>
            )}

            <div className="text-center">
              <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                Secure 256-bit encrypted checkout powered by Stripe.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
